// Phase 5 SPORE SAYS wiring: binary touch detector → says machine; Henri voice queue + SPORE symbol sounds.
import { createTrainingVideo } from './trainingVideo';
import { localVideo } from './localVideo';
import { spore } from '@/spore/runtime';
import { getAudioContext, getMaster, unlockAudio, setMuted } from '@/spore/eerieAudio';
import { createSaysMachine } from './saysMachine';
import { DRONE_OF } from './freeConfig';
import { createProfessorPreloader, generateScript, stripTags } from './professorPreloader';
import { createVoiceQueue } from './voiceQueue';
import { createDrone } from './drone';
import { createSfxBank } from './sfxBank';
import FALLBACK from './professorFallback.json';
import { createWordGuard } from './wordGuard';
import { isOff, CALM_CONFIG as CC, MEMORY_FRAGMENTS, SAYS_CONFIG } from './saysConfig';
import { SHOW_CONFIG } from './showConfig';
import { classifySymbol, TOUCH5_DEFAULTS as T5 } from '@/spore/binaryTouch';
import { outbox, clientLog } from './outbox';
import { fetchJson, netState, requestPersist } from './showCache';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const RITUAL_LINES = ['ritual.handsoff', 'ritual.touch', 'ritual.letgo', 'ritual.again', 'ritual.letgo'];
const rnd = (n) => 1 + Math.floor(Math.random() * n);
const TAP_IDS = (i) => [`tap_${i}`, `squeak_${i}`];
const STROKE_IDS = ['stroke_loop', 'pleasure_med'];
// ≋ MASSAGE: deeper, more intense moan loop (default massage_1; the audition candidates are massage_1..3)
export const MASSAGE_IDS = ['massage_1', 'massage_2', 'massage_3', 'pleasure_high'];

function createController() {
  const listeners = new Set();
  const ui = { hostHud: false, cheatSheet: false, muted: false, resetArmed: false, note: null, transmission: null, generating: null, voiceId: null, detectorWarning: null, hoverWarning: null, testing: false };
  let preloader = createProfessorPreloader();
  let sfx = createSfxBank();
  const pending = { preloader: null, sfx: null }; // a newer PINNED script / SFX bank, fully saved; swapped in on the landing page only
  let drone = null;
  let visuals = null;
  let noteTimer = null;
  let resetTimer = null;
  let strokeLoop = null;
  let massageLoop = null;
  let searchBed = null;
  // calm-down tendril game (screen taps only: never SPORE input)
  const calm = { active: false, stage: null, calm: 0, soothes: 0, triggersUsed: 0, startedAt: 0, playAt: 0, encouraged: false, relearned: false, reason: null, pending: null, loop: null };
  const notify = () => listeners.forEach((fn) => fn());
  const hostNote = (text, ms = 2500) => {
    ui.note = text;
    clearTimeout(noteTimer);
    noteTimer = setTimeout(() => {
      ui.note = null;
      notify();
    }, ms);
    notify();
  };

  const subtitleLog = (window.__spore && window.__spore.subtitleLog) || []; // last 10 subtitles, for testing: window.__spore.subtitleLog
  const queue = createVoiceQueue({
    getEntry: (id) => preloader.get(id),
    ensureVoice: (id) => preloader.ensureVoice(id),
    playOpts: { fx: false },
    cfg: { ...SHOW_CONFIG.voice, lineGapMs: SAYS_CONFIG.storyPacing.lineGapMs },
    onStart: (tx) => {
      ui.transmission = { ...tx, text: stripTags(tx.text), voiceSource: preloader.get(tx.id).voiceSource };
      subtitleLog.push({ id: tx.id, text: ui.transmission.text, phase: machine.getSnapshot().phase, level: machine.getSnapshot().level, at: new Date().toISOString(), t: Math.round(performance.now()) });
      if (subtitleLog.length > 10) subtitleLog.shift();
      notify();
    },
    onAudioEnd: () => {
      if (subtitleLog.length) subtitleLog[subtitleLog.length - 1].audioEndT = Math.round(performance.now());
    },
    onEnd: () => {
      if (ui.transmission) ui.transmission = { ...ui.transmission, ended: true };
      if (subtitleLog.length) subtitleLog[subtitleLog.length - 1].endedT = Math.round(performance.now());
      notify();
    },
  });
  const palette = (f) => {
    if (visuals) visuals.setPalette(f || 'title');
    if (drone) drone.setFeeling(DRONE_OF[f] || 'title');
  };
  const stopStroke = () => {
    sfx.fadeStop(strokeLoop);
    sfx.fadeStop(massageLoop);
    strokeLoop = null;
    massageLoop = null;
  };

  function runTouchTest(voiced) {
    ui.testing = true;
    notify();
    return spore.startTouchTest({
      onStep: (i) => {
        if (!voiced) return;
        queue.clear();
        queue.enqueue({ type: 'speak', id: RITUAL_LINES[i], kind: 'opening', priority: 3 });
      },
    }).then((res) => {
      ui.testing = false;
      if (res) {
        const pct = Math.round((res.separation || 0) * 100);
        const hp = (x) => `${Math.round((x || 0) * 100)}%`;
        const hover = `HOVER ${hp(res.hoverSeparation)} / ${hp(res.touchHoverSeparation)}`;
        ui.detectorWarning = !res.applied ? `TOUCH TEST POOR (${pct}%) · KEPT ${(res.kept || 'default').toUpperCase()} DETECTOR` : null;
        ui.hoverWarning = null; // the hover band is no longer an input
        hostNote(res.applied ? `TOUCH TEST ${res.quality.toUpperCase()} · ${res.feature} · ${pct}% · ${hover}${ui.hoverWarning ? ' · DEGRADED' : ''}` : ui.detectorWarning, 5000);
      }
      return res;
    });
  }

  function onCue(c) {
    if (c.type === 'speak') queue.enqueue(c);
    if (c.type === 'phase') {
      if (['instruct', 'demo', 'try'].includes(c.phase)) palette(c.feeling);
      if (c.phase === 'finale') palette('ecstasy');
      if (drone) drone.duck(['demo', 'try', 'unlock'].includes(c.phase));
      if (c.phase === 'ritual') runTouchTest(true).then(() => machine.ritualDone());
      if (c.phase === 'try') spore.touch.fence(spore.now()); // touches from the demo never count as, or merge into, input
      if (c.phase !== 'try') stopStroke();
      if (c.phase === 'search') {
        palette('loneliness');
        searchBed = searchBed || sfx.one(['search_bed', 'loneliness_low', 'bg_1'], { gain: 0.55, loop: true });
      } else if (searchBed) {
        sfx.fadeStop(searchBed, 0.6);
        searchBed = null;
      }
    }
    if (c.type === 'memory') sfx.one(['hover_2', 'sigh_2'], { gain: 0.6 });
    if (c.type === 'mmmm') {
      const h = sfx.one(STROKE_IDS, { gain: 0.7, loop: true }); // SPORE's soft stroke "mmmm"
      setTimeout(() => sfx.fadeStop(h, 0.4), c.ms - 500);
    }
    if (c.type === 'sing') {
      // listen-only: every symbol is told apart by ear; the visual is the same neutral pulse
      if (c.symbol === 'TAP') sfx.one(TAP_IDS(1), { gain: 1 });
      if (c.symbol === 'STROKE') {
        const h = sfx.one(STROKE_IDS, { gain: 0.95, loop: true });
        setTimeout(() => sfx.fadeStop(h), c.ms);
      }
      if (c.symbol === 'MASSAGE') {
        const h = sfx.one(MASSAGE_IDS, { gain: 1, loop: true });
        setTimeout(() => sfx.fadeStop(h), c.ms);
      }
      if (c.symbol.startsWith('OFF')) {
        sfx.one(['breath_in', 'squeak_1'], { gain: 0.9 });
        setTimeout(() => sfx.one(['breath_out', 'sigh_2'], { gain: 0.9 }), c.ms - 700);
      }
      if (visuals) visuals.gesture({ type: 'PULSE', solid: true });
    }
    if (c.type === 'wrong') {
      stopStroke();
      sfx.one([`wrong_${rnd(2)}`, 'hate_low'], { gain: 1 });
    }
    if (c.type === 'timeout') sfx.one(['sigh_out', 'sigh_1'], { gain: 0.9 });
    if (c.type === 'unlock') {
      queue.clear();
      stopStroke();
      if (!sfx.climax(c.feeling) && drone) drone.swell();
      if (visuals) visuals.bloom();
      palette(c.feeling);
    }
    if (c.type === 'guess') machine.setGuess(c.feeling, preloader.get(`${c.feeling}.interpret`).guess || 'SOMETHING?');
    if (c.type === 'end') outbox.flush();
    if (c.type === 'reset') {
      localVideo.resume();
      outbox.flush();
      queue.clear();
      stopStroke();
      sfx.fadeStop(searchBed);
      sfx.fadeStop(calm.loop);
      searchBed = null;
      Object.assign(calm, { active: false, stage: null, calm: 0, soothes: 0, triggersUsed: 0, pending: null, loop: null });
      ui.transmission = null;
      palette('title');
    }
    notify();
  }
  const machine = createSaysMachine({
    isSpeaking: () => queue.isBusy(),
    speechTail: (id) => {
      const tx = ui.transmission;
      if (!tx || tx.id !== id || tx.ended || tx.mode !== 'voice') return Infinity;
      return tx.startedAt + tx.durationMs - performance.now();
    },
    onCue,
    isDegraded: () => spore.touch.getDetector().hoverMode === 'degraded',
    // machine clock → performance.now() offsets: the guard's reads use performance.now()
    vetSolve: (reads) => {
      const off = performance.now() - spore.now();
      return guard.vetSolve(reads.map((r) => ({ word: r.word, t: r.t + off })), performance.now());
    },
  });

  // faulty-word safeguard: silent to the audience, host-only alert + amber dot
  const guard = createWordGuard({
    onChange: (word, reason, enabled) => {
      machine.setWords(guard.disabled());
      if (enabled) return notify();
      const m = machine.getSnapshot();
      ui.wordAlert = { text: `WORD DISABLED: ${word} (${reason})`, at: Date.now() };
      clientLog({ kind: 'faulty-word', name: word, message: reason, phase: m.phase, level: m.level, fallback: m.fallbackMode, adapted: m.adaptedSequences.slice(m.levelIdx).map((x) => x.join(' ')) });
      // the safeguard (not a host key) disabled a word, or hardware fallback began: SPORE is upset
      if (!String(reason).startsWith('disabled by')) triggerCalm(m.fallbackMode === 'any-input' ? 'fallback' : `faulty-word:${word.toLowerCase()}`);
      spore.saveSnapshot(`faulty-word:${word.toLowerCase()}`).then((r) => {
        ui.wordAlert = { ...ui.wordAlert, snapshot: r && (r.recording_id || (r.queued && 'queued in outbox')) };
        notify();
      });
      notify();
    },
  });
  const quiet = () => {
    const m = machine.getSnapshot();
    if (ui.screen === 'holding' || ['opening', 'demo', 'calm'].includes(m.phase)) return true;
    return m.phase === 'try' && isOff(m.sequence[m.stepIndex]);
  };
  // Henri invites touches while he teaches (rules / instruct / the massage intro): harmless practice, never judged.
  // Reads are timed at press ONSET (a human's long press then quick taps is not a metronome).
  const PRACTICE = ['rules', 'instruct', 'intro', 'memory']; // touches on a memory card are harmless practice
  const watch = (word, durationMs = 0) => guardOn(['ritual']) && !PRACTICE.includes(machine.getSnapshot().phase) && guard.observe(word, { t: performance.now() - durationMs, quiet: quiet(), live: machine.getSnapshot().phase === 'try' });
  // the word guard watches the experiments; the 4th-floor search and the ending are free for the room to move around SPORE
  const OFF_PHASES = ['title', 'ritual', 'end', 'finale', 'search', 'brooch', 'mmmm', 'closing'];
  const guardOn = (also = []) => !OFF_PHASES.filter((p) => !also.includes(p)).includes(machine.getSnapshot().phase);

  let touchStartQuiet = false;
  spore.onTouch((g) => {
    if (g.type === 'SYMBOL') watch(classifySymbol(g.durationMs).symbol, g.durationMs);
    const showOn = guardOn();
    if (g.type === 'TOUCH_START') touchStartQuiet = quiet();
    if (showOn && g.type === 'TOUCH_END' && g.reason === 'touch-over-20s') guard.capHit('touch', performance.now(), { quiet: touchStartQuiet });
    const phase = machine.getSnapshot().phase;
    const live = phase === 'try';
    if (g.type === 'TOUCH_START') {
      machine.touchStart();
      if (live) sfx.one(TAP_IDS(rnd(3)), { gain: 0.9, rate: 0.95 + Math.random() * 0.1 });
      if (visuals) visuals.gesture({ type: 'PULSE', solid: true });
    } else if (g.type === 'TOUCH_END') stopStroke();
    else if (g.type === 'SYMBOL' && !g.stale) machine.symbol(g.durationMs);
    // the HOVER band is not an input any more (no word, no sound): only TOUCH counts
  });
  spore.subscribe((e) => {
    if (e.role === 'mushroom' && e.type === 'note_on' && visuals) visuals.touch(spore.touch.getState().touching ? 90 : 12);
  });
  setInterval(() => {
    const ts = spore.touch.getState();
    machine.tick(spore.now(), ts);
    const live = machine.getSnapshot().phase === 'try';
    const hush = machine.getSnapshot().phase === 'memory'; // SPORE's ambient bed stays quiet under Henri's memory reaction
    const felt = ts.touching && !hush;
    const sig = { intensity: felt ? 0.8 : hush ? 0.04 : 0.08, activity: felt ? 0.7 : 0, agitation: 0, stillnessSeconds: felt ? 0 : 5 };
    if (visuals) visuals.setSignal(sig);
    if (drone) drone.update(sig);
    if (drone) sfx.bed(0.02, false);
    // live feedback while pressing: "mm!" on contact, the stroke loop from strokeMinMs, the deeper massage moan exactly at massageMinMs
    const tcfg = spore.touch && spore.touch.getConfig ? spore.touch.getConfig() : T5;
    if (live && ts.touching && ts.touchMs >= tcfg.strokeMinMs && ts.touchMs < tcfg.massageMinMs && !strokeLoop) strokeLoop = sfx.one(STROKE_IDS, { gain: 0.9, loop: true });
    if (live && ts.touching && ts.touchMs >= tcfg.massageMinMs && !massageLoop) {
      sfx.fadeStop(strokeLoop, 0.25);
      strokeLoop = null;
      massageLoop = sfx.one(MASSAGE_IDS, { gain: 1, loop: true });
    }
    if ((strokeLoop || massageLoop) && (!ts.touching || !live)) stopStroke();
    if (guardOn()) {
      if (ts.touching) guard.stuck('touch', ts.touchMs, performance.now());
    }
    calmTick(ts);
  }, 50);

  // ---- calm-down game ----
  const calmLog = (name, message = '') => {
    const m = machine.getSnapshot();
    clientLog({ kind: 'calm-game', name, message, phase: m.phase, level: m.level, calm: calm.calm, soothes: calm.soothes, triggersUsed: calm.triggersUsed, reason: calm.reason });
  };
  function triggerCalm(reason = 'host') {
    const manual = reason === 'host' || reason === 'test';
    const m = machine.getSnapshot();
    if (calm.active || calm.pending) return false;
    if (!manual && calm.triggersUsed >= CC.maxPerShow) return false;
    if (CC.blockedPhases.includes(m.phase) || ui.screen) {
      if (manual) hostNote(`CALM GAME NOT AVAILABLE DURING ${m.phase.toUpperCase()}`);
      return false;
    }
    if (!CC.safePhases.includes(m.phase)) {
      calm.pending = reason; // starts at the next safe boundary (instruct / demo / try)
      if (manual) hostNote('CALM GAME QUEUED · STARTS AT THE NEXT EXPERIMENT BOUNDARY');
      notify();
      return 'queued';
    }
    return startCalm(reason);
  }
  function startCalm(reason) {
    if (!machine.enterCalm()) return false;
    queue.clear();
    stopStroke();
    Object.assign(calm, { active: true, stage: 'upset', calm: 0, soothes: 0, startedAt: performance.now(), playAt: 0, encouraged: false, relearned: false, reason, pending: null });
    calm.triggersUsed += 1;
    ui.transmission = null;
    queue.enqueue({ type: 'speak', id: 'calm.upset', kind: 'finale', priority: 5 });
    queue.enqueue({ type: 'speak', id: 'calm.instruct', kind: 'finale', priority: 5 });
    calm.loop = sfx.one(['agitated_loop', 'hate_med', 'hate_low'], { gain: 0.9, loop: true });
    palette('hate');
    calmLog('start', reason);
    notify();
    return true;
  }
  function finishCalm(why) {
    calm.stage = 'done';
    sfx.fadeStop(calm.loop, 0.8);
    calm.loop = null;
    sfx.one(['sigh_2', 'pleasure_low', 'sigh_1'], { gain: 0.95 }); // contented sigh
    setTimeout(() => calm.stage === 'done' && queue.enqueue({ type: 'speak', id: 'calm.return', kind: 'finale', priority: 5 }), 1100);
    palette(machine.getSnapshot().levelFeeling);
    calmLog('calm', why);
    notify();
  }
  function calmTick(ts) {
    const phase = machine.getSnapshot().phase;
    if (calm.pending && CC.safePhases.includes(phase)) startCalm(calm.pending);
    if (!calm.active) return;
    const now = performance.now();
    if (calm.stage === 'upset' && !queue.isBusy() && now - calm.startedAt > 1500) {
      calm.stage = 'play';
      calm.playAt = now;
      notify();
    }
    if (calm.stage !== 'play') return;
    // everyone is at the screen, hands off SPORE: silently re-learn her base and watch the words again
    if (!calm.relearned && !ts.touching && !ts.hovering && now - calm.playAt > 1500) {
      calm.relearned = true;
      spore.relearnBaseline();
      const off = [...guard.disabled()];
      guard.enableAll();
      if (off.length) ui.wordAlert = { text: `CALM GAME · BASE RE-LEARNED · RE-ENABLED ${off.join(' + ')} · WATCHING AGAIN`, at: Date.now() };
      calmLog('relearn', off.join(','));
    }
    if (calm.loop) calm.loop.g.gain.setTargetAtTime(0.06 + 0.84 * (1 - calm.calm / 100), getAudioContext().currentTime, 0.3);
    if (!calm.encouraged && calm.calm >= CC.encourageAt) {
      calm.encouraged = true;
      queue.enqueue({ type: 'speak', id: 'calm.encourage', kind: 'hint', priority: 2 });
    }
    if (calm.calm >= 100) finishCalm('meter full');
    else if (now - calm.playAt >= CC.maxMs) finishCalm('auto-complete 60 s');
  }
  function soothe() {
    if (!calm.active || calm.stage !== 'play') return false;
    calm.soothes += 1;
    calm.calm = Math.min(100, Math.round((calm.soothes / CC.soothesToCalm) * 100));
    sfx.one(TAP_IDS(rnd(3)), { gain: 0.7, rate: 0.9 + Math.random() * 0.15 }); // a soft "mm"
    notify();
    return true;
  }
  function calmYes() {
    if (!calm.active || calm.stage !== 'done') return false;
    if (guard.disabled().size) guard.enableAll();
    Object.assign(calm, { active: false, stage: null });
    queue.clear();
    ui.transmission = null;
    machine.exitCalm(); // same experiment: SPORE re-sings the phrase
    calmLog('return');
    notify();
    return true;
  }
  function broochFound() {
    if (!['finale', 'search'].includes(machine.getSnapshot().phase)) return false;
    queue.clear();
    machine.broochFound();
    clientLog({ kind: 'story', name: 'brooch-found', message: `${machine.getSnapshot().discovered.length}/12 fragments` });
    notify();
    return true;
  }
  function toggleFullscreen() {
    if (document.fullscreenElement) document.exitFullscreen();
    else document.documentElement.requestFullscreen?.().catch(() => {});
  }
  // R R / the on-screen RESET button (double-tap within 3 s): back to the landing page
  function armReset(prompt = 'PRESS R AGAIN TO RESET') {
    if (ui.resetArmed) {
      ui.resetArmed = false;
      clearTimeout(resetTimer);
      if (ui.screen === 'video') video.stop();
      machine.reset();
      guard.reset(); // every word back on
      ui.wordAlert = null;
      ui.screen = null; // back to the landing page
      sfx.stopAll();
      hostNote('SHOW RESET');
    } else {
      ui.resetArmed = true;
      hostNote(prompt, 3000);
      resetTimer = setTimeout(() => {
        ui.resetArmed = false;
        notify();
      }, 3000);
    }
    notify();
  }

  async function ensureAudio() {
    await unlockAudio();
    if (!drone) {
      drone = createDrone(getAudioContext(), getMaster());
      drone.setFeeling('title');
      drone.start();
    }
    if (spore.midi.getInfo().status === 'idle') spore.midi.connect();
  }

  async function regenerate(action = 'generate') {
    if (ui.generating) return;
    if (ui.credits && !ui.credits.generation_allowed) {
      hostNote(`GENERATION LOCKED ON THIS SERVER · NO CREDITS SPENT · ${ui.credits.reason}`, 6000);
      return;
    }
    ui.generating = { texts: 0, voices: 0, total: action === 'repair' ? 6 : FALLBACK.plan.length };
    hostNote(action === 'repair' ? 'REPAIRING SCRIPT…' : 'GENERATING FRESH SCRIPT…');
    try {
      const doc = await generateScript((st) => {
        ui.generating = st;
        notify();
      }, action);
      const next = createProfessorPreloader();
      await next.run(doc);
      preloader = next;
      hostNote(`NEW SCRIPT LOADED · ${doc.chars} CHARS`, 4000);
    } catch {
      hostNote('SCRIPT GENERATION FAILED');
    }
    ui.generating = null;
    notify();
  }

  function hostTouchTest() {
    if (ui.testing) return;
    const m = machine.getSnapshot();
    const hold = !m.paused && !['title', 'end', 'ritual'].includes(m.phase);
    if (hold) machine.togglePause();
    runTouchTest(false).then(() => {
      if (hold && machine.getSnapshot().paused) machine.togglePause();
      notify();
    });
  }

  // LANDING: the button click / key is the user gesture that unlocks audio and requests MIDI for the whole show
  function startExperiment() {
    if (machine.getSnapshot().phase !== 'title') return;
    ui.screen = null;
    localVideo.pause(); // the show owns the bandwidth / CPU from here
    ensureAudio();
    machine.togglePause(); // from the title this starts Henri's opening, then the ritual
    notify();
  }
  function playVideo() {
    if (machine.getSnapshot().phase !== 'title' || ui.screen === 'video') return;
    Object.assign(ui, { videoEnd: null, screen: 'video' });
    video.start(); // synchronous inside the click / keydown: keeps the user activation for play()
    ensureAudio();
    if (drone) drone.duck(true);
    notify();
  }
  function videoDone(reason) {
    if (ui.screen !== 'video') return;
    video.stop();
    ui.screen = 'holding'; // waits for the host: START EXPERIMENT / Space / Enter
    ui.videoEnd = reason;
    if (drone) drone.duck(false);
    notify();
  }
  const skipVideo = () => videoDone('skipped');

  // C / B re-run calibration: default is to re-enable the disabled words and watch again (Shift+1/2/3 disables one again)
  function reenableForRecalibration() {
    const off = [...guard.disabled()];
    if (!off.length) return;
    guard.enableAll();
    ui.wordAlert = null;
    ui.wordAlert = { text: `RE-ENABLED ${off.join(' + ')} FOR RECALIBRATION · WATCHING AGAIN · SHIFT+1/2/3 DISABLES ONE AGAIN`, at: Date.now() };
    setTimeout(() => hostNote('WORDS RE-ENABLED · WATCHING AGAIN', 4000), 50);
  }
  // host word keys: Shift+1/2/3 (top row or numpad, any layout) toggle TAP / STROKE / MASSAGE
  const CODE_WORD = { Digit1: 'TAP', Numpad1: 'TAP', Digit2: 'STROKE', Numpad2: 'STROKE', Digit3: 'MASSAGE', Numpad3: 'MASSAGE' };
  const KEY_WORD = { '!': 'TAP', 1: 'TAP', '@': 'STROKE', '"': 'STROKE', 2: 'STROKE', '#': 'MASSAGE', '£': 'MASSAGE', '§': 'MASSAGE', 3: 'MASSAGE' };
  const wordOfKey = (e) => CODE_WORD[e.code] || KEY_WORD[e.key] || null;
  function setWord(word, enabled, by = 'host') {
    const W = String(word).toUpperCase();
    if (!['TAP', 'STROKE', 'MASSAGE'].includes(W)) return null;
    if (enabled) guard.enable(W);
    else guard.disable(W, `disabled by ${by}`, performance.now());
    ui.wordAlert = { text: `${W} ${enabled ? 'RE-ENABLED' : 'DISABLED'} BY ${by.toUpperCase()}`, at: Date.now() };
    notify();
    return guard.getStatus();
  }
  if (typeof window !== 'undefined') {
    window.addEventListener('keydown', (e) => {
      if (!e.shiftKey || e.repeat || e.ctrlKey || e.metaKey || e.altKey || isTyping(e.target)) return;
      const W = wordOfKey(e);
      if (!W) return;
      e.preventDefault();
      setWord(W, guard.disabled().has(W));
      hostNote('WORD STATUS CHANGED · SEE HUD (D)', 2500);
    }, true);
  }
  function isTyping(el) {
    return !!el && (el.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName));
  }
  function handleKey(e, navigate) {
    if (e.ctrlKey || e.metaKey || e.altKey || isTyping(e.target) || e.repeat) return;
    const k = e.key;
    const lower = k.toLowerCase();
    if (ui.screen === 'video' && lower !== 'f' && lower !== 'm') return; // the video overlay owns Esc / S
    if (ui.screen === 'holding' && k === 'Enter') {
      e.preventDefault();
      startExperiment();
      return;
    }
    const mp = machine.getSnapshot().phase;
    if (k === 'Enter' && (mp === 'search' || mp === 'finale')) return broochFound() && notify();
    if (k === 'Enter' && calm.stage === 'done') return calmYes();
    if (e.shiftKey && wordOfKey(e)) return; // handled by the capture listener below
    if (e.shiftKey && lower === 'e') {
      guard.enableAll();
      ui.wordAlert = null;
      hostNote('ALL WORDS RE-ENABLED · WATCHING AGAIN', 3000);
    } else if (lower === 'v' && e.shiftKey) {
      localVideo.download();
      hostNote(localVideo.state().local ? 'TRAINING VIDEO ALREADY SAVED ON THIS LAPTOP' : 'SAVING TRAINING VIDEO TO THIS LAPTOP…', 3000);
    } else if (lower === 'v' && machine.getSnapshot().phase === 'title') playVideo();
    else if (k === ' ') {
      e.preventDefault();
      if (machine.getSnapshot().phase === 'title') startExperiment();
      else {
        ensureAudio();
        machine.togglePause();
      }
    } else if (lower === 'u') triggerCalm('host');
    else if (lower === 'y') calmYes();
    else if (lower === 'k') broochFound();
    else if (lower === 'n') machine.unlockNow();
    else if (/^[1-9]$/.test(k)) machine.jump(Number(k) - 1);
    else if (k === '0' || k === '-' || k === '=') machine.jump({ 0: 9, '-': 10, '=': 11 }[k]);
    else if (lower === 'j') machine.nudgeNow();
    else if (lower === 'p') machine.resing();
    else if (lower === 'c') {
      reenableForRecalibration();
      hostTouchTest();
    }
    else if (k === '[' || k === ']') hostNote(`DETECTOR SENSITIVITY ${spore.setSensitivity(spore.getSensitivity() + (k === ']' ? 1 : -1))}/5`);
    else if (lower === 'b') {
      reenableForRecalibration();
      spore.relearnBaseline();
      hostNote('RESTING PLATEAU RE-LEARNED · HANDS OFF', 3000);
    } else if (lower === 'x') {
      hostNote('SAVING 60 s SNAPSHOT…');
      spore.saveSnapshot().then((r) => hostNote(r && r.recording_id ? `SNAPSHOT SAVED · ${r.recording_id} · ${r.n_events} events` : 'SNAPSHOT QUEUED IN OUTBOX · SENT WHEN ONLINE', 4000));
    } else if (lower === 'g') regenerate(e.shiftKey ? 'repair' : 'generate');
    else if (lower === 'm') {
      ui.muted = !ui.muted;
      setMuted(ui.muted);
      hostNote(ui.muted ? 'AUDIO MUTED' : 'AUDIO ON');
    } else if (lower === 'd') ui.hostHud = !ui.hostHud;
    else if (k === '?' || (e.shiftKey && (e.code === 'Slash' || k === '/'))) ui.cheatSheet = !ui.cheatSheet;
    else if (k === 'Escape') ui.cheatSheet = false;
    else if (lower === 'f') toggleFullscreen();
    else if (lower === 'l') navigate('/lab');
    else if (lower === 'r') return armReset();
    notify();
    return null;
  }

  preloader.run().then((found) => {
    // never auto-generate (it costs credits, and a fresh deployment must use its seed): built-in text, flagged for the host
    if (!found) {
      ui.scriptMissing = true;
      hostNote('NO SAVED SCRIPT ON THIS SERVER · BUILT-IN TEXT, NO VOICES · G TO GENERATE', 8000);
    }
    notify();
  });
  sfx.load().then(notify);
  // background: is there a newer PINNED version on the server? Download it fully, swap it in on the landing page only
  async function checkNewer() {
    try {
      const doc = await fetchJson('/professor/script/current', 10000);
      if (doc && doc.script_id && preloader.scriptId() && doc.script_id !== preloader.scriptId() && !pending.preloader) {
        const next = createProfessorPreloader();
        await next.run(doc);
        const p = next.progress();
        if (p.voices === p.voicesAvailable) pending.preloader = next;
      }
      const bank = await fetchJson('/sfx/bank/current', 10000);
      if (bank && bank.bank_id && sfx.progress().bankId && bank.bank_id !== sfx.progress().bankId && !pending.sfx) {
        const next = createSfxBank();
        await next.load(bank);
        if (!next.progress().sfxFailed) pending.sfx = next;
      }
    } catch {
      /* offline / slow: keep the saved copies */
    }
  }
  const landing = () => machine.getSnapshot().phase === 'title' && !ui.screen;
  setInterval(() => {
    if (!landing()) return;
    if (pending.preloader) [preloader, pending.preloader] = [pending.preloader, null];
    if (pending.sfx) [sfx, pending.sfx] = [pending.sfx, null];
  }, 1000);
  setTimeout(checkNewer, 5000);
  setInterval(() => landing() && checkNewer(), 10 * 60 * 1000);
  setInterval(() => landing() && outbox.flush(), 60 * 1000);
  // first visit: keep filling the laptop copy until it is complete (no reload needed); never competes with a running show
  setInterval(() => {
    if (!landing() && machine.getSnapshot().phase !== 'end') return;
    preloader.retryFailed();
    sfx.retryFailed();
    const lv = localVideo.state();
    if (!lv.local && ['error', 'idle'].includes(lv.dl.status)) localVideo.download();
  }, 20000);
  outbox.flush();
  requestPersist();
  // app shell saved = the page's own JS bundle is in the service-worker cache
  const shellCheck = setInterval(async () => {
    try {
      const js = performance.getEntriesByType('resource').map((e) => e.name).find((u) => u.startsWith(window.location.origin) && /\.js($|\?)/.test(u) && !u.includes('/api/'));
      if (js && typeof caches !== 'undefined' && (await caches.match(js))) {
        ui.shellSaved = true;
        clearInterval(shellCheck);
        notify();
      }
    } catch {
      /* no cache API */
    }
  }, 2000);
  localVideo.init().then(() => localVideo.autoSave());
  const video = createTrainingVideo(notify, (reason) => videoDone(reason));
  fetch(`${API}/voice/candidates`).then((r) => r.json()).then((d) => {
    ui.voiceId = d.chosen;
  }).catch(() => {});
  fetch(`${API}/credits/status`).then((r) => r.json()).then((d) => {
    ui.credits = d;
    notify();
  }).catch(() => {
    ui.credits = { generation_allowed: false, reason: 'credit status unavailable', pins: {} };
  });

  const api = {
    machine,
    guard,
    setWord,
    triggerCalm,
    soothe,
    calmYes,
    broochFound,
    toggleFullscreen,
    saveOffline: async () => {
      await requestPersist();
      localVideo.resume();
      navigator.serviceWorker && navigator.serviceWorker.controller && navigator.serviceWorker.controller.postMessage({ type: 'precache' });
      await localVideo.download();
      notify();
    },
    screenReset: () => armReset('TAP AGAIN TO RESET'),
    video,
    playVideo,
    startExperiment,
    videoDone,
    skipVideo,
    attachVisuals(v) {
      visuals = v;
      v.setPalette('title');
    },
    detachVisuals() {
      visuals = null;
    },
    handleKey,
    subscribe(fn) {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
    getSnapshot() {
      const m = machine.getSnapshot();
      const ts = spore.touch.getState();
      const vs = video.state();
      return {
        ...m,
        phase: ui.screen === 'video' || ui.screen === 'holding' ? ui.screen : m.phase === 'title' ? 'landing' : m.phase,
        machinePhase: m.phase,
        video: vs,
        videoReady: vs.ready,
        videoProgress: Math.round(vs.progress * 100) / 100,
        videoSource: vs.source || null,
        videoError: vs.error || null,
        videoPlay: vs.playResult,
        videoNeedsClick: vs.needsClick,
        videoLocal: vs.local ? `${vs.local.origin}:${vs.local.name}` : null,
        touching: ts.touching,
        hovering: ts.hovering,
        contact: ts.state3,
        touchMs: ts.touchMs,
        lastTouchSymbol: ts.lastSymbol,
        detector: ts.detector,
        touch: ts,
        touchTest: spore.getTouchTest(),
        preload: { ...preloader.progress(), ...sfx.progress() },
        offline: (() => {
          const p = preloader.progress();
          const f = sfx.progress();
          const lv = localVideo.state();
          const app = !!ui.shellSaved; // the app's own bundle is in the service-worker cache: the next load works offline
          const o = { voices: p.voices, voicesTotal: p.voicesAvailable, sfx: f.sfx, sfxTotal: f.sfxAvailable, video: !!lv.local, videoDl: lv.dl.status, videoPct: lv.dl.total ? Math.floor((lv.dl.bytes / lv.dl.total) * 100) : 0, videoError: lv.dl.error, app, persisted: lv.persisted };
          o.ready = o.voicesTotal > 0 && o.voices === o.voicesTotal && o.sfxTotal > 0 && o.sfx === o.sfxTotal && o.video && app;
          return o;
        })(),
        net: { ...netState(), outbox: outbox.state().count, outboxSent: outbox.state().sent, outboxError: outbox.state().lastError },
        scriptId: preloader.scriptId(),
        voiceSources: preloader.voiceSources(),
        voiceQueue: queue.getState(),
        voiceId: ui.voiceId,
        muted: ui.muted,
        sensitivity: spore.getSensitivity(),
        audioReady: !!drone,
        wordStatus: guard.getStatus(),
        memoryFragments: m.discovered.map((f) => ({ n: m.feelings.indexOf(f) + 1, feeling: f, text: MEMORY_FRAGMENTS[f] })),
        calmGame: { active: calm.active, calm: calm.calm, triggersUsed: calm.triggersUsed, stage: calm.stage, soothes: calm.soothes, reason: calm.reason, pending: calm.pending, relearned: calm.relearned, agitation: calm.active ? 100 - calm.calm : 0 },
        ui: { ...ui },
      };
    },
  };
  spore.setShowProvider(() => api.getSnapshot());
  return api;
}

export const getShowController = () => window.__sporeShow || (window.__sporeShow = createController());
